import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl } from "@/lib/api";
import { Stars } from "@/components/Stars";
import { HOME } from "@/constants/testIds";

const caption = (r) => r.pet_name || r.name || "A happy customer";

const share = async (e, r) => {
  e.preventDefault();
  e.stopPropagation();
  const url = `${window.location.origin}/product/${r.slug}#review-${r.id}`;
  const text = `${r.pet_name ? `Meet ${r.pet_name}` : "Look at this happy pet"} with the ${r.product_name} from PetTok Shop`;
  if (navigator.share) {
    try { return await navigator.share({ title: "Pets of PetTok", text, url }); } catch (err) { if (err.name === "AbortError") return; }
  }
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied. Share it anywhere!");
  } catch {
    window.prompt("Copy this link:", url);
  }
};

// Approved review photos from buyers; hidden until at least one exists.
export const PetsWall = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/reviews/photos").then(({ data }) => setItems(data)).catch(() => {}); }, []);
  if (!items.length) return null;
  return (
    <section data-testid={HOME.petsWall} className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E06D44]">From our customers</p>
      <h2 className="mt-2 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">Pets of PetTok</h2>
      <p className="mt-3 text-stone-600 max-w-xl">Real pets, real toys, real reviews. Kiddee approves of every single one of them.</p>
      <div className="mt-10 columns-2 md:columns-3 lg:columns-4 gap-4 [&>*]:mb-4">
        {items.map((r, i) => (
          <Link
            key={r.id}
            to={`/product/${r.slug}`}
            data-testid={HOME.petsWallItem(r.id)}
            className="group relative block break-inside-avoid overflow-hidden rounded-[1.5rem] bg-[#F0EBE1] fade-up"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <button
              type="button"
              data-testid={HOME.petsWallShare(r.id)}
              onClick={(e) => share(e, r)}
              aria-label={`Share ${caption(r)}'s photo`}
              className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-white/90 text-[#1C1917] grid place-items-center shadow hover:bg-white hover:scale-110 transition-[transform,background-color]"
            >
              <Share2 size={16} />
            </button>
            <img src={imgUrl(r.photo)} alt={`${caption(r)} with ${r.product_name}`} loading="lazy" className="w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/70 to-transparent text-white">
              <Stars value={r.rating} size={12} />
              <p className="mt-1 text-sm font-semibold">{caption(r)}</p>
              <p className="text-xs text-white/80 truncate">{r.product_name}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};
